import { useCallback, useMemo, useRef, useState } from 'react';
import {
    DndContext,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconGripVertical } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import { formatFileSize } from '../compress/compressPdfEngine';
import { mergePdfFiles } from './mergePdfEngine';
import { applyPdfPageEdits, pagesHaveEdits } from '../shared/pdfPageEditEngine';
import PdfDropZone from '../../components/pdf/PdfDropZone';
import { PdfFileRow } from '../../components/pdf/PdfFileRow';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import PdfDocPreview from '../../components/pdf/PdfDocPreview';
import PdfPageEditor from '../../components/pdf/PdfPageEditor';
import { usePdfPageEdits, fileKeyOf } from '../../components/pdf/usePdfPageEdits';
import ToolReadyHint from '../../components/common/ToolReadyHint';
import {
    deriveToolStatus,
    downloadBlob,
    createToolItemId,
    getToolStatusText,
} from '../../toolPageUtils';
import { printBlob } from '../../../../../utils/generateDocPdf';
import { usePdfPaste } from '../../components/common/useFilePaste';

const MIN_FILES = 2;
const MAX_FILE_BYTES = 500 * 1024 * 1024;

function SortableFileRow({ item, index, total, onRemove, selected, onSelect, t }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: item.id });

    return (
        <div
            ref={setNodeRef}
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
                opacity: isDragging ? 0.55 : 1,
            }}
        >
            <PdfFileRow
                name={item.file.name}
                meta={formatFileSize(item.file.size)}
                selected={selected}
                onSelect={onSelect}
                onRemove={() => onRemove(item.id)}
                removeLabel={t('mergePdf.removeFile')}
                dragHandle={(
                    <button
                        type="button"
                        className="cursor-grab border-none bg-transparent p-0 active:cursor-grabbing"
                        aria-label={t('mergePdf.reorder')}
                        {...attributes}
                        {...listeners}
                    >
                        <IconGripVertical size={16} />
                    </button>
                )}
            />
        </div>
    );
}

export default function MergePdf() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);

    const [items, setItems] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [progress, setProgress] = useState(null);
    const [previewIndex, setPreviewIndex] = useState(0);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const files = useMemo(() => items.map((item) => item.file), [items]);
    const clearResult = useCallback(() => {
        setResult(null);
        setError(null);
        setProgress(null);
    }, []);
    const { pagesByKey, updatePagesForKey, resetPageEdits } = usePdfPageEdits(files, clearResult);

    const addFiles = useCallback((incoming) => {
        const list = Array.from(incoming || []);
        const pdfs = list.filter((f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
        if (!pdfs.length) {
            const msg = t('mergePdf.errors.invalidType');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        let tooLarge = false;
        setItems((prev) => {
            const next = [...prev];
            for (const file of pdfs) {
                if (file.size > MAX_FILE_BYTES) {
                    tooLarge = true;
                    continue;
                }
                if (next.some((item) => item.file.name === file.name && item.file.size === file.size)) continue;
                next.push({ id: createToolItemId(), file });
            }
            return next;
        });

        if (tooLarge) {
            const msg = t('mergePdf.errors.tooLarge');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } else {
            setError(null);
        }
        setResult(null);
    }, [t]);

    // Ctrl/Cmd+V anywhere on the page drops a copied PDF straight in.
    usePdfPaste(addFiles);

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        addFiles(e.dataTransfer.files);
    };

    const removeItem = (id) => {
        setItems((prev) => {
            const index = prev.findIndex((item) => item.id === id);
            setPreviewIndex((p) => (index < p ? p - 1 : Math.max(0, Math.min(p, prev.length - 2))));
            return prev.filter((item) => item.id !== id);
        });
        setResult(null);
        setError(null);
    };

    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        setItems((prev) => {
            const oldIndex = prev.findIndex((item) => item.id === active.id);
            const newIndex = prev.findIndex((item) => item.id === over.id);
            return arrayMove(prev, oldIndex, newIndex);
        });
        setResult(null);
    };

    const handleReset = () => {
        setItems([]);
        setResult(null);
        setError(null);
        setProgress(null);
        setPreviewIndex(0);
        resetPageEdits();
    };

    const handleMerge = async () => {
        if (items.length < MIN_FILES || processing) return;

        // Guard: a file whose every page was removed cannot be merged.
        for (const file of files) {
            const pages = pagesByKey[fileKeyOf(file)];
            if (Array.isArray(pages) && pages.length && pages.every((p) => p.removed)) {
                const msg = t('mergePdf.errors.removeAll');
                setError(msg);
                notifications.show({ title: t('common.error'), message: msg, color: 'red' });
                return;
            }
        }

        recordToolUsage('pdf/merge', { category: 'pdf' });

        setProcessing(true);
        setResult(null);
        setError(null);

        try {
            // Apply per-page edits (rotate / crop / delete / reorder / duplicate) before merging.
            const sources = [];
            for (const file of files) {
                const pages = pagesByKey[fileKeyOf(file)];
                sources.push(
                    Array.isArray(pages) && pagesHaveEdits(pages)
                        ? await applyPdfPageEdits(file, pages)
                        : file,
                );
            }

            const blob = await mergePdfFiles(sources, (fileIndex, fileTotal) => {
                setProgress({ fileIndex, fileTotal, pct: Math.round((fileIndex / fileTotal) * 100) });
            });

            const baseName = files[0].name.replace(/\.pdf$/i, '');
            setResult({
                name: `${baseName}-merged.pdf`,
                size: blob.size,
                blob,
            });
            notifications.show({ title: t('common.success'), message: t('mergePdf.done'), color: 'green' });
        } catch (err) {
            let msg = t('mergePdf.errors.failed');
            if (err?.message === 'password') msg = t('mergePdf.errors.password');
            else if (err?.message === 'removeAll') msg = t('mergePdf.errors.removeAll');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setProcessing(false);
            setProgress(null);
        }
    };

    const status = deriveToolStatus({
        hasInput: items.length > 0,
        canRun: items.length >= MIN_FILES,
        processing,
        hasResult: !!result,
        hasError: !!error,
    });

    const howToSteps = useMemo(() => [
        t('mergePdf.howTo.step1'),
        t('mergePdf.howTo.step2'),
        t('mergePdf.howTo.step3'),
        t('mergePdf.howTo.step4'),
    ], [t]);

    const processingLabel = progress
        ? t('mergePdf.progress', {
            file: toBnDigits(progress.fileIndex),
            files: toBnDigits(progress.fileTotal),
        })
        : t('mergePdf.merging');

    const statusText = getToolStatusText(status, t, {
        empty: t('mergePdf.previewEmpty'),
        ready: t('mergePdf.previewReady', { count: toBnDigits(items.length) }),
        success: t('mergePdf.done'),
    });

    const successLabel = result
        ? t('mergePdf.resultMeta', {
            files: toBnDigits(items.length),
            size: formatFileSize(result.size),
        })
        : '';

    return (
        <ToolPageLayout
            slug="pdf/merge"
            subtitleKey="mergePdf.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('toolPage.files', { count: toBnDigits(items.length) })}
            showAdd={items.length > 0}
            onAdd={() => fileInputRef.current?.click()}
            howToSteps={howToSteps}
            onReset={items.length > 0 || result || error ? handleReset : undefined}
            resetLabel={t('toolPage.reset')}
            matchPreviewHeight={items.length > 0}
            primaryAction={{
                label: t('mergePdf.merge'),
                onClick: handleMerge,
                disabled: items.length < MIN_FILES || processing || !!result,
                loading: processing,
                icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M8 6l4-3 4 3M8 18l4 3 4-3M12 3v18M3 12h18" />
                    </svg>
                ),
            }}
            controls={(
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/pdf,.pdf"
                        multiple
                        className="hidden"
                        onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                    />

                    {items.length === 0 ? (
                        <PdfDropZone
                            title={t('mergePdf.dropTitle')}
                            hint={t('mergePdf.dropHint')}
                            isDragging={isDragging}
                            onClick={() => fileInputRef.current?.click()}
                            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handleDrop}
                        />
                    ) : (
                        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
                            <div className="min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-contain pr-1 [scrollbar-width:thin]">
                                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                    <SortableContext items={items.map((item) => item.id)} strategy={verticalListSortingStrategy}>
                                        {items.map((item, index) => (
                                            <SortableFileRow
                                                key={item.id}
                                                item={item}
                                                index={index}
                                                total={items.length}
                                                onRemove={removeItem}
                                                selected={previewIndex === index}
                                                onSelect={() => setPreviewIndex(index)}
                                                t={t}
                                            />
                                        ))}
                                    </SortableContext>
                                </DndContext>
                            </div>
                            <p className="flex flex-none items-center gap-1.5 text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M5 9l-3 3 3 3M9 5l3-3 3 3M15 19l-3 3-3-3M19 9l3 3-3 3" />
                                </svg>
                                {t('mergePdf.orderHint')}
                            </p>
                            {items.length === 1 ? (
                                <p className="flex-none text-xs text-[#C98A11]">{t('mergePdf.needMore')}</p>
                            ) : null}
                        </div>
                    )}
                </>
            )}
            preview={(
                <ToolPreviewPanel
                    title={t('mergePdf.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('mergePdf.previewEmpty')}
                    idleHint={t('mergePdf.previewEmptyHint')}
                    processingLabel={processingLabel}
                    progress={progress?.pct}
                    error={error}
                    successLabel={successLabel}
                    downloadLabel={t('mergePdf.resultTitle')}
                    onDownload={result ? () => downloadBlob(result.blob, result.name) : undefined}
                    onPrint={result ? () => printBlob(result.blob) : undefined}
                    onEditAgain={result ? clearResult : undefined}
                    editAgainLabel={t('toolPage.editAgain')}
                    onAgain={result ? handleReset : undefined}
                    againLabel={t('mergePdf.startOver')}
                    readyHint={status === 'ready' && items.length >= MIN_FILES ? <ToolReadyHint actionLabel={t('mergePdf.merge')} /> : null}
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handleDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                    showIdle={items.length === 0 && !error}
                    pdfPreview={(!!result || items.length > 0) && !processing && !error}
                >
                    {result && !processing && !error ? (
                        <PdfDocPreview blob={result.blob} />
                    ) : items.length > 0 && !processing && !error ? (
                        <PdfPageEditor
                            files={files}
                            pagesByKey={pagesByKey}
                            onPagesChangeForKey={updatePagesForKey}
                            activeIndex={previewIndex}
                            onActiveIndexChange={setPreviewIndex}
                        />
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
